import { useEffect, useRef, useState } from "react";
import { useCallStore } from "../state/call.js";
import { isIosDevice, routeToSpeaker, speakerRouteRunning, stopSpeakerRoute } from "../calling/callAudio.js";
import { getAudioContext } from "../calling/tones.js";

function fmtElapsed(totalSeconds: number): string {
  const m = Math.floor(totalSeconds / 60);
  const s = totalSeconds % 60;
  return `${m}:${s.toString().padStart(2, "0")}`;
}

// Small hand-rolled icons — no icon library dependency, just enough detail to
// read clearly at button size.
function PhoneIcon() {
  return (
    <svg viewBox="0 0 24 24" width="26" height="26" fill="currentColor">
      <path d="M6.6 10.8c1.4 2.8 3.8 5.2 6.6 6.6l2.2-2.2c.3-.3.7-.4 1.1-.3 1.2.4 2.5.6 3.8.6.6 0 1 .4 1 1v3.6c0 .6-.4 1-1 1C10.5 21.5 2.5 13.5 2.5 3.7c0-.6.4-1 1-1h3.6c.6 0 1 .4 1 1 0 1.3.2 2.6.6 3.8.1.4 0 .8-.3 1.1z" />
    </svg>
  );
}
function PhoneHangupIcon() {
  return (
    <svg viewBox="0 0 24 24" width="26" height="26" fill="currentColor" transform="rotate(135 12 12)">
      <path d="M6.6 10.8c1.4 2.8 3.8 5.2 6.6 6.6l2.2-2.2c.3-.3.7-.4 1.1-.3 1.2.4 2.5.6 3.8.6.6 0 1 .4 1 1v3.6c0 .6-.4 1-1 1C10.5 21.5 2.5 13.5 2.5 3.7c0-.6.4-1 1-1h3.6c.6 0 1 .4 1 1 0 1.3.2 2.6.6 3.8.1.4 0 .8-.3 1.1z" />
    </svg>
  );
}
function SpeakerIcon({ on }: { on: boolean }) {
  return (
    <svg viewBox="0 0 24 24" width="22" height="22" fill="currentColor">
      <path d="M4 9v6h4l5 4V5L8 9H4z" />
      {on ? (
        <path d="M16 8.5a5 5 0 0 1 0 7M18.5 6a8.5 8.5 0 0 1 0 12" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" />
      ) : (
        <path d="M16 12h5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      )}
    </svg>
  );
}

function MicIcon({ off }: { off: boolean }) {
  return (
    <svg viewBox="0 0 24 24" width="22" height="22" fill="currentColor">
      <path d="M12 15a3 3 0 0 0 3-3V6a3 3 0 0 0-6 0v6a3 3 0 0 0 3 3z" />
      <path d="M19 11a1 1 0 1 0-2 0 5 5 0 0 1-10 0 1 1 0 1 0-2 0 7 7 0 0 0 6 6.92V20H9a1 1 0 1 0 0 2h6a1 1 0 1 0 0-2h-2v-2.08A7 7 0 0 0 19 11z" />
      {off && <path d="M3 3l18 18" stroke="currentColor" strokeWidth="2" />}
    </svg>
  );
}
function CameraIcon({ off }: { off: boolean }) {
  return (
    <svg viewBox="0 0 24 24" width="22" height="22" fill="currentColor">
      <path d="M4 6h11a2 2 0 0 1 2 2v1.5l3.4-2A1 1 0 0 1 22 8.3v7.4a1 1 0 0 1-1.6.8L17 14.5V16a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2z" />
      {off && <path d="M3 3l18 18" stroke="currentColor" strokeWidth="2" />}
    </svg>
  );
}

/**
 * Renders nothing when idle; otherwise a full-screen takeover, phone-call
 * style. "ended" is a brief notice ("No answer") before it disappears.
 */
export function CallOverlay() {
  const {
    phase,
    peerUsername,
    ringingConfirmed,
    video,
    videoEnabled,
    muted,
    localStream,
    remoteStream,
    error,
    reconnecting,
    remoteTracks,
    speaker,
    toggleSpeaker,
    acceptCall,
    declineCall,
    cancelCall,
    hangUp,
    toggleMute,
    toggleVideo,
  } = useCallStore();
  const remoteVideoRef = useRef<HTMLVideoElement>(null);
  const selfVideoRef = useRef<HTMLVideoElement>(null);
  const audioRef = useRef<HTMLAudioElement>(null);
  const [elapsed, setElapsed] = useState(0);
  // iPhone: play the call through the loudspeaker via Web Audio (see
  // calling/callAudio.ts); the <audio> element then stays muted so the voice
  // isn't doubled. If Web Audio can't start, fall back to the element.
  const ios = isIosDevice();
  const [elementMuted, setElementMuted] = useState(false);
  useEffect(() => {
    if (!ios || !remoteStream || !speaker) {
      stopSpeakerRoute();
      setElementMuted(false);
      return;
    }
    const routed = routeToSpeaker(remoteStream);
    setElementMuted(routed);
    if (!routed) return;
    const check = setTimeout(() => {
      if (!speakerRouteRunning()) {
        stopSpeakerRoute();
        setElementMuted(false);
      }
    }, 1500);
    return () => clearTimeout(check);
  }, [ios, remoteStream, remoteTracks, speaker]);
  useEffect(() => () => stopSpeakerRoute(), []);

  useEffect(() => {
    if (remoteVideoRef.current) remoteVideoRef.current.srcObject = remoteStream;
    if (audioRef.current) audioRef.current.srcObject = remoteStream;
    // Also re-run when `phase` flips the stage's ref target between self/remote.
  }, [remoteStream, phase]);

  useEffect(() => {
    if (selfVideoRef.current) selfVideoRef.current.srcObject = localStream;
  }, [localStream, phase]);

  // iOS pauses media elements while the phone is locked and doesn't always
  // resume them — restart playback when the app is back on screen.
  useEffect(() => {
    const resume = () => {
      if (document.visibilityState !== "visible") return;
      for (const el of [audioRef.current, remoteVideoRef.current, selfVideoRef.current]) {
        if (el?.paused && el.srcObject) void el.play().catch(() => undefined);
      }
      void getAudioContext()?.resume().catch(() => undefined);
    };
    document.addEventListener("visibilitychange", resume);
    return () => document.removeEventListener("visibilitychange", resume);
  }, []);

  useEffect(() => {
    if (phase !== "connected") {
      setElapsed(0);
      return;
    }
    const startedAt = Date.now();
    const t = setInterval(() => setElapsed(Math.floor((Date.now() - startedAt) / 1000)), 1000);
    return () => clearInterval(t);
  }, [phase]);

  if (phase === "idle") return null;

  // While ringing outgoing with video, the "stage" is your own camera (there's
  // no remote stream yet) — same as FaceTime showing your own preview before
  // the other side answers. Once connected, the stage is the remote camera
  // and your own becomes the small corner self-view.
  const showVideoStage = video && (phase === "connected" || phase === "outgoing");
  const stageIsSelf = phase === "outgoing";

  return (
    <div className="call-overlay">
      <audio ref={audioRef} autoPlay muted={elementMuted} />

      {showVideoStage && (
        <video
          ref={stageIsSelf ? selfVideoRef : remoteVideoRef}
          className={stageIsSelf ? "call-video-stage mirrored" : "call-video-stage"}
          autoPlay
          playsInline
          // Picture only: the <audio> element (or Web Audio) carries the
          // voice, so it isn't played twice.
          muted
        />
      )}
      {showVideoStage && !stageIsSelf && (
        <video ref={selfVideoRef} className="call-video-self" autoPlay playsInline muted />
      )}

      <div className={showVideoStage ? "call-video-topbar" : "call-card"}>
        {!showVideoStage && <span className="avatar call-avatar">{(peerUsername ?? "?").slice(0, 1).toUpperCase()}</span>}
        <div className="call-name">{peerUsername}</div>
        <div className="call-status">
          {phase === "outgoing" && (ringingConfirmed ? "Ringing…" : "Calling…")}
          {phase === "incoming" && (video ? "Incoming video call" : "Incoming call")}
          {phase === "connected" && (reconnecting ? "Reconnecting…" : fmtElapsed(elapsed))}
          {phase === "ended" && (error ?? "Call ended")}
        </div>
      </div>

      <div className={showVideoStage ? "call-video-controls" : "call-controls"}>
        {phase === "incoming" && (
          <>
            <button className="icon-btn decline" onClick={declineCall} aria-label="Decline">
              <PhoneHangupIcon />
            </button>
            <button className="icon-btn accept" onClick={() => void acceptCall()} aria-label="Accept">
              <PhoneIcon />
            </button>
          </>
        )}
        {phase === "outgoing" && (
          <button className="icon-btn decline" onClick={cancelCall} aria-label="Cancel">
            <PhoneHangupIcon />
          </button>
        )}
        {phase === "connected" && (
          <>
            <button className="icon-btn ghost" onClick={toggleMute} aria-label={muted ? "Unmute" : "Mute"}>
              <MicIcon off={muted} />
            </button>
            {ios && (
              <button
                className={`icon-btn ghost ${speaker ? "active" : ""}`}
                onClick={toggleSpeaker}
                aria-label={speaker ? "Switch to earpiece" : "Switch to speaker"}
                title={speaker ? "Speaker" : "Earpiece"}
              >
                <SpeakerIcon on={speaker} />
              </button>
            )}
            {video && (
              <button
                className="icon-btn ghost"
                onClick={toggleVideo}
                aria-label={videoEnabled ? "Turn camera off" : "Turn camera on"}
              >
                <CameraIcon off={!videoEnabled} />
              </button>
            )}
            <button className="icon-btn decline" onClick={hangUp} aria-label="Hang up">
              <PhoneHangupIcon />
            </button>
          </>
        )}
      </div>
    </div>
  );
}
